import { defineColumns } from './piColumns.js';
import { DISTRIBUTION_BANDS } from './piEngagementModel.js';
import { CLASSIFICATIONS, NO_CLASSIFICATION } from './piFeaturesModel.js';
import { ONBOARDING_STAGES } from './piOnboardingModel.js';
import { COMMERCIAL_STATES, OPPORTUNITY_STATUSES } from './piOpportunitiesModel.js';
import { CADENCE_LABELS, PATTERN_GROUPS } from './piPatternsModel.js';
import { JOURNEY_OPTIONS, MODULE_OPTIONS, PLATFORM_OPTIONS, ROLE_CLASS_OPTIONS, SIZE_BAND_FILTER_OPTIONS, platformNote } from './piQuery.js';
import { DROP_DESCRIPTION } from './piCompaniesModel.js';
import { STATE_INFO } from './piState.js';

export const CATEGORIES_SECTION = 'categorias';

const ROLE_TEXT = {
  1: 'Colaborador comum: bate ponto, pede ajuste, assina documento.',
  2: 'Gestor de equipe: aprova ajustes e férias da equipe.',
  3: 'Administrador da empresa: configura e opera o sistema.',
  4: 'Contador ou escritório contábil com acesso à empresa.',
};

const SIZE_TEXT = {
  A: '100 ou mais funcionários informados no cadastro.',
  B: 'De 50 a 99 funcionários.',
  C: 'De 21 a 49 funcionários.',
  D: 'De 11 a 20 funcionários.',
  E: 'Até 10 funcionários.',
};

const PROFILE_VALUES = ['A', 'B', 'C', 'D', 'E'].map((letter) => ({
  label: 'Perfil ' + letter,
  text: 'Classificação dada pelo time no cadastro da empresa.',
}));

const PLAN_VALUES = [
  { label: 'Mensal' }, { label: 'Bimestral' }, { label: 'Trimestral' }, { label: 'Semestral' },
  { label: 'Anual' }, { label: 'Bianual' },
  { label: 'Não informado', text: 'Assinatura sem periodicidade registrada.' },
  { label: 'Sem assinatura', text: 'Nenhuma assinatura registrada para a empresa.' },
];

const SITUATION_VALUES = [
  { label: 'Ativa', text: 'Empresa ativa e fora das internas: entra no universo dos números.' },
  { label: 'Trial', text: 'Em período de teste.' },
  { label: 'Bloqueada', text: 'Acesso bloqueado (normalmente por pagamento).' },
  { label: 'Expirada', text: 'Assinatura ou teste vencido.' },
  { label: 'Cancelada', text: 'Cancelada no cadastro.' },
  { label: 'Inativa', text: 'Fora do universo ativo; aparece só se teve atividade no período, sem ativos e sem taxa.' },
];

const STRENGTH_VALUES = [
  { label: 'Forte', text: 'Pedido explícito do cliente (pedido de contato ou cliques na tela bloqueada).' },
  { label: 'Moderada', text: 'Uso que indica necessidade (teste em uso, complemento do que já usa).' },
  { label: 'Fraca', text: 'Só comparação com pares do mesmo porte.' },
];

const optionValues = (options, textOf) => options.map((option) => ({
  label: option.label,
  text: textOf ? textOf(option) || '' : '',
}));

const stateValues = (keys) => keys.map((key) => ({ label: STATE_INFO[key].label, text: STATE_INFO[key].description }));

export const CATEGORY_GROUPS = [
  {
    key: 'empresa',
    label: 'Da empresa',
    hint: 'Cortes que classificam a conta. Porte e perfil são coisas diferentes: porte é tamanho, perfil é a classificação manual do time.',
    items: [
      {
        key: 'porte',
        name: 'Porte',
        question: 'Qual o tamanho da empresa?',
        source: 'Número de funcionários informado no cadastro da empresa (Total de funcionários), lido na foto diária. Não é o número de ativos nem o de assentos contratados.',
        values: SIZE_BAND_FILTER_OPTIONS.map((option) => ({ label: option.label, text: SIZE_TEXT[option.value] || '' })),
        usedIn: 'Filtro em quase todas as abas, corte de assentos no Engajamento, pares no detalhe da empresa, regra de pares em Oportunidades.',
        filter: true,
      },
      {
        key: 'perfil',
        name: 'Perfil da empresa',
        question: 'Como o time classificou esta conta?',
        source: 'Campo "Perfil" (A a E) do cadastro da empresa no Master, preenchido manualmente pelo time. Não é calculado pelo painel e não tem relação com o porte.',
        values: PROFILE_VALUES.concat([{ label: 'Sem perfil', text: 'Empresa sem perfil preenchido no cadastro.' }]),
        usedIn: 'Filtro "Perfil da empresa", corte "Por porte e perfil da empresa" no detalhe da funcionalidade, lista de empresas.',
        filter: true,
      },
      {
        key: 'jornada',
        name: 'Jornada',
        question: 'Em que etapa da jornada o time colocou o cliente?',
        source: 'Campo "Jornada do cliente" do cadastro da empresa no Master, lido na foto diária. É informativo: o painel não calcula nem corrige a jornada.',
        values: JOURNEY_OPTIONS.map((option) => ({ label: option.label, text: option.value === '0' ? 'Trial no sistema.' : '' })),
        usedIn: 'Filtro "Jornada" das listas de empresas no Engajamento e em Empresas, e detalhe da empresa.',
        filter: true,
      },
      {
        key: 'banco',
        name: 'Banco',
        question: 'Em qual banco de clientes a empresa está?',
        source: 'Código de 4 letras do banco (shard) onde a empresa mora, como TTTT ou XXXX. Serve para suporte e para abrir a conta no Master; não é segmento de negócio.',
        values: [],
        usedIn: 'Lista de empresas, detalhe da empresa e Status (jobs por banco).',
        filter: false,
      },
      {
        key: 'situacao',
        name: 'Situação',
        question: 'A empresa está no universo dos números?',
        source: 'Situação da conta na foto diária. Empresas internas ficam sempre fora.',
        values: SITUATION_VALUES,
        usedIn: 'Lista de empresas e detalhe da empresa.',
        filter: false,
      },
      {
        key: 'plano',
        name: 'Tipo de plano',
        question: 'Qual a periodicidade da assinatura?',
        source: 'Periodicidade da assinatura vigente na foto diária.',
        values: PLAN_VALUES,
        usedIn: 'Grupos de padrão de uso e oportunidades.',
        filter: false,
      },
      {
        key: 'faixa-ativos',
        name: 'Faixa de usuários ativos',
        question: 'Quantas pessoas usam de verdade?',
        source: 'Usuários distintos com ação significativa nos 30 dias terminando no último dia fechado.',
        values: optionValues(DISTRIBUTION_BANDS),
        usedIn: 'Distribuição de empresas no Engajamento; clicar numa faixa filtra a lista.',
        filter: true,
      },
      {
        key: 'sinal',
        name: 'Sinal da empresa',
        question: 'A conta está esfriando?',
        source: 'Comparação dos ativos em 30 dias com o período anterior.',
        values: [{ label: 'Queda de atividade', text: DROP_DESCRIPTION }],
        usedIn: 'Listas de empresas no Engajamento e em Empresas, com o filtro "Só com queda de atividade".',
        filter: true,
      },
      {
        key: 'funil',
        name: 'Etapa de ativação',
        question: 'Até onde a empresa vendida chegou?',
        source: 'Etapa mais avançada atingida até D+15, D+30 ou D+60, contando da data da venda (D0). Cada etapa implica as anteriores; '
          + 'quem bateu ponto sem configurar turno aparece como "pulou a configuração". Ativada = 1ª batida de colaborador.',
        values: ONBOARDING_STAGES.map((stage) => ({ label: stage.stage + ' · ' + stage.label, text: stage.text })),
        usedIn: 'Barras "Onde as empresas param", empresas travadas e ficha da empresa (aba Ativação).',
        filter: false,
      },
    ],
  },
  {
    key: 'usuario',
    label: 'Do usuário e do uso',
    hint: 'Cortes que classificam quem usa e por onde usa.',
    items: [
      {
        key: 'papel',
        name: 'Papel do usuário',
        question: 'Quem fez a ação?',
        source: 'Papel do usuário no dia, pelo perfil de permissão dele na empresa. No dia cada pessoa conta em um papel só; na janela de 7 ou 30 dias, quem mudou de papel aparece nos dois.',
        values: optionValues(ROLE_CLASS_OPTIONS, (option) => ROLE_TEXT[option.value]),
        usedIn: 'Filtro "Papel do usuário", assentos e segmentação no Engajamento, corte no detalhe da funcionalidade.',
        filter: true,
      },
      {
        key: 'plataforma',
        name: 'Plataforma',
        question: 'Por onde a ação foi feita?',
        source: 'Canal informado no evento (web, app, tablet, batida por QR Code etc.). Na API nova, quando o evento não informa o canal, a web é reconhecida pelo endereço de onde a tela foi aberta (V2 ou painel antigo; o MarQWeb ainda não tem canal próprio e conta como Web); o resto fica em Desconhecida, nunca em App. Desconhecida = evento sem canal; Sistema = ação automática, sem pessoa.',
        values: optionValues(PLATFORM_OPTIONS, (option) => platformNote(option.value)),
        usedIn: 'Filtro em Visão geral, Engajamento e Operações.',
        filter: true,
      },
      {
        key: 'ativo',
        name: 'Ativo x em uso',
        question: 'A pessoa agiu ou só abriu?',
        source: 'Ativo = fez ação significativa (inclui a batida). Em uso = abriu o sistema, mesmo sem agir; o login automático do tablet MarQTab (a cada 15 min) não conta. A diferença aparece como "abriram sem agir".',
        values: [
          { label: 'Ativo', text: 'Fez pelo menos uma ação significativa na janela.' },
          { label: 'Em uso', text: 'Abriu o sistema na janela, com ou sem ação (o login automático do tablet MarQTab não conta).' },
          { label: 'Abriram sem agir', text: 'Em uso menos ativos.' },
        ],
        usedIn: 'Engajamento e detalhe da empresa.',
        filter: false,
      },
    ],
  },
  {
    key: 'funcionalidade',
    label: 'Da funcionalidade',
    hint: 'Como cada funcionalidade é agrupada e lida.',
    items: [
      {
        key: 'modulo',
        name: 'Módulo',
        question: 'A que área do produto pertence?',
        source: 'Módulo do evento no catálogo de eventos.',
        values: optionValues(MODULE_OPTIONS),
        usedIn: 'Filtro em Visão geral, Engajamento, Funcionalidades e Operações.',
        filter: true,
      },
      {
        key: 'classificacao',
        name: 'Classificação da adoção',
        question: 'Como a adoção está andando?',
        source: 'Regra sobre a adoção do período contra o período anterior.',
        values: optionValues(CLASSIFICATIONS.concat([NO_CLASSIFICATION]), (option) => option.description),
        usedIn: 'Aba Funcionalidades.',
        filter: true,
      },
      {
        key: 'cadencia',
        name: 'Cadência esperada',
        question: 'Com que frequência faz sentido usar?',
        source: 'Definida no catálogo de funcionalidades; decide a janela do padrão de uso.',
        values: Object.keys(CADENCE_LABELS).map((key) => ({ label: CADENCE_LABELS[key], text: '' })),
        usedIn: 'Padrões de uso no detalhe da funcionalidade.',
        filter: false,
      },
      {
        key: 'padrao',
        name: 'Grupo de padrão de uso',
        question: 'Quem usa bem e quem usa pouco?',
        source: 'Períodos com uso dentro da janela da cadência, por empresa.',
        values: optionValues(PATTERN_GROUPS, (option) => option.description),
        usedIn: 'Padrões de uso no detalhe da funcionalidade.',
        filter: false,
      },
    ],
  },
  {
    key: 'comercial',
    label: 'Comerciais',
    hint: 'Visíveis só para quem tem acesso a receita.',
    items: [
      {
        key: 'estado-comercial',
        name: 'Estado comercial',
        question: 'A empresa tem a funcionalidade e pagou por ela?',
        source: 'Deduzido das liberações e vendas registradas; não há item de contrato por funcionalidade.',
        values: optionValues(COMMERCIAL_STATES),
        usedIn: 'Portfólio no detalhe da empresa e Oportunidades.',
        filter: false,
      },
      {
        key: 'forca',
        name: 'Força do sinal',
        question: 'Quão forte é o motivo da oportunidade?',
        source: 'Vem da regra que gerou o card.',
        values: STRENGTH_VALUES,
        usedIn: 'Ordem dos cards em Oportunidades.',
        filter: false,
      },
      {
        key: 'status-oportunidade',
        name: 'Status da oportunidade',
        question: 'Em que ponto está o contato?',
        source: 'Atualizado por quem trabalha o card.',
        values: optionValues(OPPORTUNITY_STATUSES),
        usedIn: 'Oportunidades.',
        filter: true,
      },
    ],
  },
  {
    key: 'dado',
    label: 'Do dado',
    hint: 'Estado de cada número: diz se ele já pode ser lido como definitivo.',
    items: [
      {
        key: 'estado-numero',
        name: 'Estado do número',
        question: 'Posso confiar neste número?',
        source: 'Calculado junto com cada indicador e mostrado ao lado dele.',
        values: stateValues(['ok', 'partial', 'derived', 'immature', 'insufficient', 'no_usage', 'no_eligible', 'disabled', 'not_instrumented', 'na']),
        usedIn: 'Todas as abas.',
        filter: false,
      },
    ],
  },
];

export const categoryCount = () => CATEGORY_GROUPS.reduce((total, group) => total + group.items.length, 0);

export const searchCategories = (query) => {
  const text = typeof query === 'string' ? query.trim().toLowerCase() : '';
  if (!text) return CATEGORY_GROUPS;
  const matches = (value) => typeof value === 'string' && value.toLowerCase().indexOf(text) !== -1;
  return CATEGORY_GROUPS
    .map((group) => Object.assign({}, group, {
      items: group.items.filter((item) => matches(item.name) || matches(item.question) || matches(item.source)
        || item.values.some((value) => matches(value.label) || matches(value.text))),
    }))
    .filter((group) => group.items.length);
};

export const categoryLink = (key) => ({
  path: '/product-intelligence/status',
  query: { secao: CATEGORIES_SECTION },
  hash: key ? '#pi-cat-' + key : '',
});

const valuesText = (values) => (Array.isArray(values) ? values : [])
  .map((value) => value.label + (value.text ? ' — ' + value.text : ''))
  .join('; ');

export const categoryTableRows = (groups) => {
  const rows = [];
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    group.items.forEach((item) => rows.push({
      key: group.key + ':' + item.key,
      group: group.label,
      name: item.name,
      question: item.question,
      source: item.source,
      values: valuesText(item.values),
      usedIn: item.usedIn,
      filter: item.filter === true,
    }));
  });
  return rows;
};

export const CATEGORY_COLUMNS = defineColumns([
  { key: 'group', label: 'Grupo', type: 'text', infoKey: 'status.categoryGroup' },
  { key: 'name', label: 'Categoria', type: 'text', infoKey: 'status.categoryName' },
  { key: 'question', label: 'Pergunta', type: 'text', infoKey: 'status.categoryName' },
  { key: 'source', label: 'De onde vem', type: 'text', infoKey: 'status.categorySource' },
  { key: 'values', label: 'Valores', type: 'text', infoKey: 'status.categoryValues' },
  { key: 'usedIn', label: 'Onde aparece', type: 'text', infoKey: 'status.categoryUsedIn' },
  { key: 'filter', label: 'Filtro', type: 'text', infoKey: 'status.categoryFilter' },
]);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piCategoriesModel.js